import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/use-toast';
import { describeFailure } from '@/lib/describe-failure';
import OwnersServiceAPI from 'shared/api/owners.service.api';
import { formatFullName } from 'shared/utils';
import { getPolicyTechOwners, setPolicyTechOwners } from '@/api/organization.api.private';
import { useOrganizationContext } from '../organization.context';

const EMPTY_HINT = 'When empty, PolicyTech content is owned by the system user.';

const formatUserLabel = (user, options) => formatFullName(user, options) || user.id;

// Sets internalConfigurations.policyTech.ownerIds. The COLLABORATOR+ list is only a
// suggestion — the server re-validates and rejects the whole write on an ineligible id.
export default function PolicyTechOwnersSettings() {
  const { organization } = useOrganizationContext();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [persistedIds, setPersistedIds] = useState([]);

  const load = useCallback(
    async (isCancelled = () => false) => {
      setLoading(true);
      setLoadError(null);

      try {
        // The org page already switched the admin token, so this lists this org's users.
        const [currentRes, eligibleRes] = await Promise.all([
          getPolicyTechOwners(organization.id),
          OwnersServiceAPI.getAll(),
        ]);
        if (isCancelled()) return;

        const failures = [
          currentRes.error && { label: 'current owners', res: currentRes },
          eligibleRes.error && { label: 'eligible users', res: eligibleRes },
        ].filter(Boolean);

        if (failures.length) {
          failures.forEach(({ label, res }) =>
            console.error('Failed to load PolicyTech owners settings', {
              organizationId: organization.id,
              resource: label,
              code: res.code,
            }),
          );
          toast({
            title: 'Failed to load PolicyTech owners',
            description: describeFailure(failures[0].res),
            variant: 'destructive',
          });
          // Save is a full replace — editing off a failed read would wipe the real owners.
          setLoadError(
            failures.map(({ label, res }) => `${label} (${describeFailure(res)})`).join('; '),
          );
          return;
        }

        // Duplicate ids would share a React key and toggle as one row.
        const owners = [...new Map((currentRes.data?.owners ?? []).map(o => [o.id, o])).values()];
        setSelected(owners);
        setPersistedIds(owners.map(owner => owner.id));
        setEligibleUsers(
          (eligibleRes.data?.owners ?? [])
            .filter(user => !user.isDeactivated)
            .sort((a, b) => formatUserLabel(a).localeCompare(formatUserLabel(b))),
        );
      } catch (error) {
        if (isCancelled()) return;
        console.error('Failed to load PolicyTech owners settings', {
          organizationId: organization.id,
          error,
        });
        toast({
          title: 'Failed to load PolicyTech owners',
          description: 'An error occurred while loading the settings',
          variant: 'destructive',
        });
        setLoadError('Something went wrong loading the settings.');
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [organization.id, toast],
  );

  useEffect(() => {
    let cancelled = false;
    load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Owners who lost eligibility stay listed and removable, flagged so the admin
  // knows the save will be rejected until they're gone.
  const options = useMemo(() => {
    const eligibleIds = new Set(eligibleUsers.map(user => user.id));
    const staleOwners = selected.filter(user => !eligibleIds.has(user.id));
    return [
      ...eligibleUsers.map(user => ({ user, stale: false })),
      ...staleOwners.map(user => ({ user, stale: true })),
    ];
  }, [eligibleUsers, selected]);

  const selectedIds = new Set(selected.map(user => user.id));
  const persistedIdSet = new Set(persistedIds);
  const isDirty =
    selectedIds.size !== persistedIdSet.size ||
    [...selectedIds].some(id => !persistedIdSet.has(id));

  const toggleUser = user => {
    setSelected(prev =>
      prev.some(({ id }) => id === user.id)
        ? prev.filter(({ id }) => id !== user.id)
        : [...prev, user],
    );
  };

  const onSave = async () => {
    setSaving(true);

    try {
      const res = await setPolicyTechOwners({
        id: organization.id,
        ownerIds: selected.map(user => user.id),
      });

      if (res.error) {
        console.error('Failed to update PolicyTech owners', {
          organizationId: organization.id,
          code: res.code,
        });
        toast({
          title: 'Failed to update PolicyTech owners',
          description: describeFailure(res),
          variant: 'destructive',
        });
        return;
      }

      setPersistedIds(res.data?.ownerIds ?? selected.map(user => user.id));
      toast({
        status: 'success',
        title: 'PolicyTech owners updated',
      });
    } catch (error) {
      console.error('Failed to update PolicyTech owners', {
        organizationId: organization.id,
        error,
      });
      toast({
        title: 'Failed to update PolicyTech owners',
        description: 'An error occurred while saving the settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Rendered as the "Content owners" section of the combined PolicyTech card.
  return (
    <div className="space-y-4">
      {loadError ? (
        <div className="space-y-2">
          <p className="text-sm text-destructive">
            Couldn&apos;t load the current owners: {loadError}
          </p>
          <p className="text-xs text-muted-foreground">
            Editing is disabled so the existing configuration isn&apos;t overwritten.
          </p>
          <Button variant="outline" onClick={() => load()}>
            Retry
          </Button>
        </div>
      ) : loading ? (
        <div className="text-xs text-muted-foreground">Loading owners...</div>
      ) : (
        <>
          <div className="space-y-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  id="policytech-owners"
                  type="button"
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                  <span
                    className={`min-w-0 truncate text-left ${selected.length ? '' : 'text-muted-foreground'}`}>
                    {selected.length
                      ? `${selected.length} owner${selected.length === 1 ? '' : 's'} selected`
                      : 'Select owners...'}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0 opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="max-h-72 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto"
                align="start">
                {options.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No eligible users — grant someone COLLABORATOR or higher in this organization
                    first.
                  </div>
                ) : (
                  options.map(({ user, stale }) => (
                    <DropdownMenuCheckboxItem
                      key={user.id}
                      checked={selectedIds.has(user.id)}
                      onCheckedChange={() => toggleUser(user)}
                      onSelect={event => event.preventDefault()}>
                      {/* The trigger shows only a count, so rows carry the email. */}
                      {formatUserLabel(user, { withEmail: true })}
                      {stale && (
                        <span className="ml-1 text-xs text-destructive">— no longer eligible</span>
                      )}
                    </DropdownMenuCheckboxItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selected.map(user => (
                  <Badge key={user.id} variant="secondary" className="gap-1 font-normal">
                    {formatUserLabel(user)}
                    <button
                      type="button"
                      aria-label={`Remove ${formatUserLabel(user)}`}
                      onClick={() => toggleUser(user)}
                      className="rounded-full hover:text-destructive focus:outline-none">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">{EMPTY_HINT}</p>
          </div>
          <Button onClick={onSave} loading={saving} disabled={saving || !isDirty || !!loadError}>
            Save
          </Button>
        </>
      )}
    </div>
  );
}
